import { useState, useRef, useEffect } from "react";
import { parseActiveFacet, scrollToId } from "../../../helpers/GlobalHelpers";

/**
 * Custom hook for managing search filters and facets
 */
export const useSearchFilters = () => {
  const [activeFacets, setActiveFacets] = useState([]);
  const [filterFacetGroups, setFilterFacetGroups] = useState(null);
  const [mobileFilterMenuOpen, setMobileFilterMenuOpen] = useState(false);
  const [mobileSelectedFacetGroup, setMobileSelectedFacetGroup] = useState(null);
  const [mobileSelectedToggleGroup, setMobileSelectedToggleGroup] = useState(null);
  
  /**
   * Ref to store facet name mappings (groupId -> facetId -> name)
   * Persists across renders to avoid rebuilding on every search
   */
  const facetNameMap = useRef({});
  
  /**
   * Ref to store search trigger callbacks from parent component
   * Allows useEffect to access current callbacks without adding them as dependencies
   */
  const searchCallbacks = useRef({ setLoadingResults: null, setPageNum: null, setReRunSearch: null });
  
  /**
   * Ref to track the type of facet change ('add', 'remove', 'removeAll', or null)
   * Used to determine if search should be triggered when activeFacets changes
   */
  const facetChangeType = useRef(null);

  /**
   * Automatically trigger search when activeFacets changes from user actions
   * Uses refs to track change type and access callbacks without stale closures
   */
  useEffect(() => {
    // Only trigger search if there was an explicit facet change and callbacks are available
    if (facetChangeType.current && 
        searchCallbacks.current.setLoadingResults && 
        searchCallbacks.current.setPageNum && 
        searchCallbacks.current.setReRunSearch) {
      searchCallbacks.current.setLoadingResults(true);
      searchCallbacks.current.setPageNum(1);
      searchCallbacks.current.setReRunSearch((prev) => !prev);
      facetChangeType.current = null; // Reset after triggering
    }
  }, [activeFacets]);

  /**
   * Loop through returned facets and find what is active
   */
  const setupActiveFacets = (facetGroups, updateActiveFacets = true) => {
    let tempFacetArray = [];
    let groupMap = {};

    let toggleFacetGroups = window.globalProperties.toggleFacetGroups;

    facetGroups.forEach((group) => {
      const groupId = group.id;
      groupMap[groupId] = {};

      let matchingToggleGroup1 = toggleFacetGroups.find(
        (facetGroup) => facetGroup.option1SearchFacetGroupId === groupId
      );
      let matchingToggleGroup2 = toggleFacetGroups.find(
        (facetGroup) => facetGroup.option2SearchFacetGroupId === groupId
      );

      if (group.facets) {
        group.facets.forEach((facet) => {
          // build global map to retrieve name property later on when needed
          groupMap[groupId][facet.id] = { name: facet.name };

          if (facet.selected) {
            if (matchingToggleGroup1) {
              facet.mobileName = matchingToggleGroup1.option1Label
                ? `${facet.name} ${matchingToggleGroup1.option1Label}`
                : null;
              facet.desktopName = matchingToggleGroup1.option1ToggleLabel
                ? `${facet.name} ${matchingToggleGroup1.option1ToggleLabel}`
                : null;
            }

            if (matchingToggleGroup2) {
              facet.mobileName = matchingToggleGroup2.option2Label
                ? `${facet.name} ${matchingToggleGroup2.option2Label}`
                : null;
              facet.desktopName = matchingToggleGroup2.option2ToggleLabel
                ? `${facet.name} ${matchingToggleGroup2.option2ToggleLabel}`
                : null;
            }

            tempFacetArray.push({
              id: groupId + ":" + facet.id,
              name: facet.name,
              mobileName: facet.mobileName,
              desktopName: facet.desktopName,
            });
          }
        });
      }
    });

    // Always update the facet name map
    facetNameMap.current = groupMap;
    
    // Only update active facets if requested (e.g., on initial load)
    if (updateActiveFacets) {
      setActiveFacets(tempFacetArray);
    }
  };

  /**
   * Remove a facet from active facets
   */
  const removeFacet = (facet, isMobileNav, setLoadingResults, setPageNum, setReRunSearch) => {
    // Store callbacks for useEffect to use
    searchCallbacks.current = { setLoadingResults, setPageNum, setReRunSearch };
    
    setActiveFacets((currentFacets) => {
      const activeFacetsClone = Symbol.iterator in Object(currentFacets) ? [...currentFacets] : [];
      const index = activeFacetsClone.findIndex((obj) => obj.id === facet);
      if (index !== -1) {
        activeFacetsClone.splice(index, 1);
        facetChangeType.current = 'remove'; // Signal that search should trigger
        return activeFacetsClone;
      }
      return currentFacets;
    });

    // Scroll to top of results after clearing filters
    scrollToId("searchTop", isMobileNav);

    // Scroll the inner scrollable div to the top
    const leftRail = document.querySelector(".search__left-rail");
    if (leftRail) {
      leftRail.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  /**
   * Add item to facet list
   */
  const addFacet = (facet, setLoadingResults, setPageNum, setReRunSearch) => {
    // Store callbacks for useEffect to use
    searchCallbacks.current = { setLoadingResults, setPageNum, setReRunSearch };
    
    setActiveFacets((currentFacets) => {
      const activeFacetsClone = Symbol.iterator in Object(currentFacets) ? [...currentFacets] : [];
      const index = activeFacetsClone.findIndex((obj) => obj.id === facet);

      if (index === -1) {
        const { groupId, facetId } = parseActiveFacet(facet);

        activeFacetsClone.push({
          id: facet,
          name: facetNameMap.current[groupId][facetId].name || "",
        });

        facetChangeType.current = 'add'; // Signal that search should trigger
        return activeFacetsClone;
      }
      return currentFacets;
    });
  };

  /**
   * Remove all facets
   */
  const removeAllFacet = (removeUrlFilters, removeFilterFromPageURL, isMobileNav, setLoadingResults, setPageNum, setReRunSearch) => {
    // Store callbacks for useEffect to use
    searchCallbacks.current = { setLoadingResults, setPageNum, setReRunSearch };
    facetChangeType.current = 'removeAll';

    setActiveFacets([]);

    if (removeUrlFilters) {
      removeFilterFromPageURL();
    }

    // Scroll to top of results after clearing filters
    scrollToId("searchTop", isMobileNav);

    // Scroll the inner scrollable div to the top
    const leftRail = document.querySelector(".search__left-rail");
    if (leftRail) {
      leftRail.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  /**
   * Build final facet groups with toggle support
   */
  const buildFinalFacetGroups = (facetGroups) => {
    const toggleFacetGroups = window.globalProperties.toggleFacetGroups;
    let finalGroups = [];
    let toggleProperties = [];

    toggleFacetGroups.forEach((tg) => {
      facetGroups.forEach((fg) => {
        let toggleGroupParent = finalGroups.find((t) => t.name === tg.name);

        let newFacetGroup = null;
        let togglePropertyFound = false;

        let toggle1PropertyFound =
          fg.id === tg.option1FacetGroup[0] || fg.id === tg.option1SearchFacetGroupId;
        let toggle2PropertyFound =
          fg.id === tg.option2FacetGroup[0] || fg.id === tg.option2SearchFacetGroupId;

        //determine if we need to create a toggle group
        if (toggle1PropertyFound) {
          toggle2PropertyFound =
            facetGroups.findIndex((item) => item.id === tg.option2FacetGroup[0]) !== -1 ||
            facetGroups.findIndex((item) => item.id === tg.option2SearchFacetGroupId) !== -1;
        } else if (toggle2PropertyFound) {
          toggle1PropertyFound =
            facetGroups.findIndex((item) => item.id === tg.option1FacetGroup[0]) !== -1 ||
            facetGroups.findIndex((item) => item.id === tg.option1SearchFacetGroupId) !== -1;
        }

        if (toggle1PropertyFound && toggle2PropertyFound) {
          if (toggleProperties.indexOf(fg.id) === -1) {
            toggleProperties.push(fg.id);
          }
          //create a new togglegroup object
          let toggleGroup = {
            id: fg.id,
            type: "toggle",
            facets: [],
          };

          let togglePosition = null;

          //add correct labels
          if (fg.id === tg.option1FacetGroup[0] || fg.id === tg.option1SearchFacetGroupId) {
            togglePosition = 1;
            toggleGroup.mobileName = tg.option1Label;
            toggleGroup.desktopName = tg.option1ToggleLabel;
          }
          if (fg.id === tg.option2FacetGroup[0] || fg.id === tg.option2SearchFacetGroupId) {
            togglePosition = 2;
            toggleGroup.mobileName = tg.option2Label;
            toggleGroup.desktopName = tg.option2ToggleLabel;
          }
          //add facets from facet group to toggle group
          fg.facets.forEach((f) => {
            f.mobileName = toggleGroup.mobileName ? `${f.name} ${toggleGroup.mobileName}` : f.name;
            f.desktopName = toggleGroup.desktopName ? `${f.name} ${toggleGroup.desktopName}` : f.name;
            toggleGroup.facets.push(f);
          });
          //create a parent facet group for toggleGroups if it doesn't already exist
          if (!toggleGroupParent) {
            newFacetGroup = {};
            newFacetGroup.name = tg.name;
            newFacetGroup.id = tg.name.toLowerCase();
            newFacetGroup.toggleGroups = [];
          }
          //add toggle group to parent facet group
          if (newFacetGroup) {
            newFacetGroup.toggleGroups.push(toggleGroup);
          } else {
            //inserts the toggle group to align with the order of the toggle labels
            toggleGroupParent.toggleGroups.splice(togglePosition - 1, 0, toggleGroup);
          }

          togglePropertyFound = true;
        }
        //add the facet group to the array
        if (togglePropertyFound && !toggleGroupParent) {
          newFacetGroup.facets = null;
          finalGroups.push(newFacetGroup);
        } else if (!togglePropertyFound) {
          //only add the facet group if it doesn't already exist in finalGroups
          if (finalGroups.findIndex((item) => item.id === fg.id) === -1) {
            finalGroups.push(fg);
          }
        }
      });
    });

    //because we potentially looped through multiple toggle group properties above, we may have duplicated values between facet groups and facet groups within a toggle group.
    //remove those facet groups that are part of a toggle group.
    toggleProperties.forEach((item) => {
      let fgIndex = finalGroups.findIndex((fg) => fg.id === item);
      if (fgIndex > -1) {
        finalGroups.splice(fgIndex, 1);
      }
    });
    setFilterFacetGroups(finalGroups);
  };

  /**
   * Close mobile filter menu
   */
  const closeFilters = () => {
    setMobileFilterMenuOpen(false);
    setMobileSelectedFacetGroup(null);
    setMobileSelectedToggleGroup(null);
  };

  return {
    activeFacets,
    setActiveFacets,
    filterFacetGroups,
    setFilterFacetGroups,
    mobileFilterMenuOpen,
    setMobileFilterMenuOpen,
    mobileSelectedFacetGroup,
    setMobileSelectedFacetGroup,
    mobileSelectedToggleGroup,
    setMobileSelectedToggleGroup,
    facetNameMap,
    setupActiveFacets,
    removeFacet,
    addFacet,
    removeAllFacet,
    buildFinalFacetGroups,
    closeFilters,
  };
};
